import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { Tag, Tooltip } from 'antd';
import ApprovalBar from 'components/ApprovalBar';
import EntityAttachments from 'components/EntityAttachments';
import EntityChangeRequests from 'components/EntityChangeRequests';
import EntityLogo from 'components/EntityLogo';
import EntityLogoEditor from 'components/EntityLogoEditor';
import FormSelect from 'components/Form/FormSelect';
import HmsEntityFormController from 'components/Form/HmsEntityFormController';
import HmsEntityFormGridBasic from 'components/Form/HmsEntityFormGridBasic';
import HmsField from 'components/Form/HmsField';
import HmsTabbedTables from 'components/HmsTabbedTables';
import { ListContext } from 'components/ListsProvider';
import PlayerAwards from 'components/Player/PlayerAwards';
import PlayerDisciplinary from 'components/Player/PlayerDisciplinary';
import PlayerDuplicityCheck from 'components/Player/PlayerDuplicityCheck';
import PlayerGames from 'components/Player/PlayerGames';
import PlayerJerseys from 'components/Player/PlayerJerseys';
import PlayerPositions from 'components/Player/PlayerPositions';
import PlayerSeasonPlayerRegistrations from 'components/Player/PlayerSeasonPlayerRegistrations';
import PlayerSponsors from 'components/Player/PlayerSponsors';
import PlayerTeams from 'components/Player/PlayerTeams';
import PlayerLevelTag from 'components/PlayerLevelTag';
import OrgLink from 'components/System/OrgLink';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import useEntities from 'hooks/useEntities';
import useFetchEntities from 'hooks/useFetchEntities';
import useLists from 'hooks/useLists';
import useLoadEntity from 'hooks/useLoadEntity';
import useOrganization from 'hooks/useOrganization';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import useLoginStore from 'store/loginStore';
import useNotificationStore from 'store/notificationStore';

dayjs.extend(utc);

const Player = () => {
  const isAdmin = useLoginStore(s => s.isAdmin)();
  const isTeamManager = useLoginStore(s => s.isTeamManager)();
  const entities = useEntities();
  const entity = entities.Player;
  const { organization } = useOrganization();
  const ORGANIZATION_ID = organization?.organizationId;
  const addMessage = useNotificationStore((state) => state.addMessage);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [seasonRegistrationsKey, setSeasonRegistrationsKey] = useState(0);
  const [emailDirty, setEmailDirty] = useState(false);
  
  const { id, isLoading, isReloading, relationsLoading, relationsReloading, isError, isNotFound, data, reload, relationsData, reloadRelations } = useLoadEntity({
    entity,
    relations: [
      'Sponsors',
      'Awards',
      'Jerseys>Team',
      'ListPositions',
      'User(email,userId)',
    ].join(','),
  });
  const { relationsData: relationsTeamsOnly, reloadRelations: reloadRelationsTeamsOnly } = useLoadEntity({
    entity,
    relations: [
      'Teams',
    ].join(','),
  });
  const { relationsData: relationsDisciplinariesOnly, reloadRelations: reloadRelationsDisciplinariesOnly } = useLoadEntity({
    entity,
    relations: [
      'Disciplinaries(subject,status),Disciplinaries>Game(startDate,startTime),Disciplinaries>Game>HomeTeam(short),Disciplinaries>Game>AwayTeam(short),Disciplinaries>Game>Venue(name)',
    ].join(','),
  });
  const { data: gamesData } = useFetchEntities('Lineup', {
    filters: {
      playerId: id
    },
    extraAttributes: 'StatGoals,StatAssists,StatPoints,StatPenaltyMinutes,IsStar',
    relations: 'Game!(*,+HomeTeamGoals,+AwayTeamGoals),Game!>HomeTeam,Game!>AwayTeam,Game!>Venue,ListPosition(name)',
  });
  const { relationsData: attachmentsData, reloadRelations: reloadAttachments } = useLoadEntity({
    entity,
    relations: [
      'Attachments',
    ].join(',')
  });

  const { orgPreferences } = useContext(ListContext);

  const needsToSelectTeam = !isAdmin && !data?.status && (orgPreferences.requiredFields?.includes("teamId") || isTeamManager);
  const { data: teamsData, isLoading: teamsDataLoading } = 
    useFetchEntities(needsToSelectTeam && 'Team', { onlyAttributes: ['teamId', 'name', 'logoUrl'] });

  // Fetch number of season registrations for this player
  const { data: seasonRegistrationsData, reload: reloadSeasonRegistrations } = useFetchEntities('SeasonPlayerRegistration', {
    filters: { playerId: id },
    onlyAttributes: ['seasonPlayerRegistrationId'],
  });
  const seasonRegistrationsCount = seasonRegistrationsData?.length ?? 0;

  const ref = useRef();
  const tabbedTablesRef = useRef(null);
  const tabbedTablesParentRef = useRef(null);

  // Predvyplní tým práve jednou, jakmile seznam týmu skutecne dorazí. Drív se
  // hlídal jen `teamsDataLoading`, jenze ten pri prvním nactení nemusí zmenit
  // hodnotu (useFetch.js: isLoading se inicializuje z url a pro první fetch se
  // uz nenastavuje), takze se efekt nemusel spustit ve chvíli, kdy uz data byla.
  const teamsPrefilled = useRef(false);
  useEffect(() => {
    if (id != '_new_' || teamsPrefilled.current) {
      return;
    }
    if (teamsDataLoading == false && teamsData?.length) {
      teamsPrefilled.current = true;
      ref?.current?.setFieldValue('Teams', teamsData.map(t => t.teamId));
    }
  }, [teamsDataLoading, teamsData, id]);

  // Reset email dirty state when data changes
  useEffect(() => {
    setEmailDirty(false);
  }, [data?.email]);

  // Change requests
  const { relationsData: changeRequestsData, reloadRelations: reloadChangeRequests } = useLoadEntity({
    entity,
    relations: [
      'ChangeRequests',
    ].join(',')
  });
  const unprocessedChangeRequestCount = useMemo(() => changeRequestsData?.ChangeRequests?.filter(r => r.status == 'NEW')?.length ?? 0, [changeRequestsData]);
  const showChangeRequestDot = isAdmin && unprocessedChangeRequestCount > 0;
  const isLocked = (data && data?.status != 'DRAFT' && data?.status != 'INVITED') && !isAdmin;
  const canCreateChangeRequest = isLocked && !isAdmin;
  const { ListPlayerLevel } = useLists();
  const ListPlayerLevelWithIcons = ListPlayerLevel.map((l) => ({
    value: l[entities.ListPlayerLevel.primaryKey],
    label: <span className="flex gap-2 items-center"><PlayerLevelTag listPlayerLevel={l} /> {l.name}</span>
  }));
  const changeRequestFields = [
    { value: "listPlayerLevelId", label: 'Player level', type: 'select', options: ListPlayerLevelWithIcons },
    { value: 'firstName', },
    { value: "lastName", },
    { value: "birthday", },
    { value: "gender", },
    { value: "stick", },
    { value: "status", },
    { value: "height", },
    { value: "weight", },
    { value: "phone", },
    { value: "email", },
    { value: "countryBirth", },
    { value: "cityBirth", },
    { value: "country", },
    { value: "city", },
    { value: "publicProfileUrl", },
    { value: "AddTeam", label: 'Add Team' },
    { value: "RemoveTeam", label: 'Remove Team' },
    { value: "Position", label: 'Position' },
    { value: "Jersey", label: 'Jersey' },
    { value: "Other", label: 'Other / Comment' },
  ].map(f => ({
    ...f,
    label: f.label ?? entity.fields[f.value].displayName,
    type: f.type ?? 'text',
  }));

  const LinkToTab = ({ tab, children }) => (
    <a
      className="underline"
      onClick={() => {
        if (tabbedTablesRef.current) {
          tabbedTablesRef.current.setActiveKey(tab);
          if (tabbedTablesParentRef.current) {
            tabbedTablesParentRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }}
    >
      {children}
    </a>
  );


  // // Invite to HMS functionality
  // const handleInviteToHMS = async () => {
  //   if (!data?.playerId || !data?.email) {
  //     addMessage({ text: 'Player must have an email address to be invited', type: 'error' });
  //     return;
  //   }

  //   // if (data?.userId) {
  //   //   addMessage({ text: 'Player already has a user account', type: 'error' });
  //   //   return;
  //   // }

  //   setInviteLoading(true);
  //   try {
  //     const res = await bulkSendInvite({ 
  //       organizationId: ORGANIZATION_ID, 
  //       playerIds: [data.playerId] 
  //     });
  //     const json = await res.json();
      
  //     // Extract message from the first item in the response
  //     const firstItemMessage = json.results?.[0]?.message || json.message;
      
  //     if (json.status === 'OK') {
  //       addMessage({ text: firstItemMessage || 'Invitation sent successfully', type: 'success' });
  //       // Reload the player data to reflect the new status
  //       reload();
  //     } else if (json.status === 'PARTIAL_ERROR') {
  //       addMessage({ text: firstItemMessage || 'Some invitations failed', type: 'warning' });
  //     } else if (json.status === 'ERROR') {
  //       addMessage({ text: firstItemMessage || 'All invitations failed', type: 'error' });
  //     } else {
  //       addMessage({ text: firstItemMessage || 'Error sending invitation', type: 'error' });
  //     }
  //   } catch (e) {
  //     addMessage({ text: 'Error communicating with the server', type: 'error' });
  //   } finally {
  //     setInviteLoading(false);
  //   }
  // };

  return (
    <>
      <HmsEntityFormController
        {... {
          ref, entity, id, isLoading: isLoading || teamsDataLoading, isReloading, isError,
          isNotFound, data, reload, canCreateChangeRequest, isLocked,
          changeRequestFields, reloadChangeRequests
        }}
        topRightText={relationsData?.User?.email ?
          <OrgLink to={`/settings/users/${relationsData.User.userId}`}>
            <Tooltip title="Click to open user account">
              <Tag color="green">
                <CheckOutlined /> User of HMS
              </Tag>
            </Tooltip>
          </OrgLink>
          :
          <Tooltip title="This user has not been invited to create a HMS account yet.">
            <Tag color="orange" className="cursor-pointer">
              Not a user of HMS
            </Tag>
          </Tooltip>
        }
        header={({ isDirty, lastValidationErrors, data: formData }) => {
          if (isLoading || relationsLoading) {
            return;
          }

          const missingItems = [];
          if (id == '_new_') {
            missingItems.push('Complete this form and save your changes.');
          } else {
            if (isDirty) {
              missingItems.push('Save your changes.');
            } else {
              if (lastValidationErrors.length > 0) {
                missingItems.push('Fix validation errors');
              }
            }
          }
          if (orgPreferences.requiredFields.includes('jerseyNumber') && (relationsData?.Jerseys?.length ?? 0) == 0) {
            missingItems.push(
              <span>
                Assign at least one <LinkToTab tab="Jerseys">Jersey</LinkToTab>
              </span>
            );
          }
          if (orgPreferences.requiredFields.includes('listPositionId') && (relationsData?.ListPositions?.length ?? 0) == 0) {
            missingItems.push(
              <span>
                Assign at least one <LinkToTab tab="Positions">Position</LinkToTab>
              </span>
            );
          }

          return (
            <>
              <PlayerDuplicityCheck
                data={formData}
                id={id}
              />

              <ApprovalBar
                status={data?.status}
                relationsData={relationsData}
                missingItems={missingItems}
                entity={entity}
                isDirty={isDirty}
                id={id}
                reload={reload}
              />
            </>)
        }}
        // buttons={[
        //   (isAdmin || isTeamManager) && data?.email && (!data?.userId || data?.status === 'INVITED') && {
        //     label: 'Invite To HMS',
        //     icon: <UserAddOutlined />,
        //     onClick: () => {
        //       AntdModal.confirm({
        //         title: 'Are you sure you want to invite this player to HMS?',
        //         content: data?.userId ? 'This will resend the invitation email to the existing user account.' : 'This will create a user account and send an invitation email.',
        //         okText: 'Yes, invite',
        //         okType: 'primary',
        //         cancelText: 'No, cancel',
        //         onOk: handleInviteToHMS,
        //         confirmLoading: inviteLoading,
        //       });
        //     },
        //     loading: inviteLoading,
        //     type: 'primary'
        //   }
        // ].filter(Boolean)}
        form={(
          <>
            <div className="flex flex-col desktop:flex-row w-full">
              <div className="flex items-center justify-center ml-4 mr-6 mb-5 desktop:mb-0 ">
                <EntityLogoEditor entity={entities.Player} id={id} data={data} size={112} disabled={false /* isLocked */} />
              </div>
              <div className="flex-auto">
                            <HmsEntityFormGridBasic>
              <HmsField name="firstName" fieldItemProps={{ disabled: isLocked }} />
              <HmsField name="lastName" fieldItemProps={{ disabled: isLocked }} />
              <HmsField name="birthday" fieldItemProps={{ disabled: isLocked }} required={!isAdmin && orgPreferences.requiredFields?.includes("birthday")} />
              <HmsField name="gender" fieldItemProps={{ disabled: isLocked }} required={!isAdmin && orgPreferences.requiredFields?.includes("gender")} />
              <HmsField name="stick" fieldItemProps={{ disabled: isLocked }} required={!isAdmin && orgPreferences.requiredFields?.includes("stick")} />
              <FormSelect name="listPlayerLevelId" label="Player level" options={ListPlayerLevelWithIcons} fieldItemProps={{ disabled: isLocked }}
                required={!isAdmin && orgPreferences.requiredFields?.includes("listPlayerLevelId")} />
              <HmsField name="status" fieldItemProps={{ disabled: !isAdmin || isLocked }} />
            </HmsEntityFormGridBasic>
            {/* <HmsEntityFormGridBasic>
              <div className="mb-2">
                <div className="labelCol !p-0 !pb-1">
                  <span className="text-gray-500 custom-form-label-text-size">Username:</span>
                </div>
                <div className="text-gray-900">
                  {relationsData?.User?.email ? relationsData.User.email : 'No user assigned'}
                </div>
              </div>
            </HmsEntityFormGridBasic> */}
              </div>
            </div>

            <h4>Personal</h4>
            <HmsEntityFormGridBasic>
              <HmsField name="height" addonAfter="cm" fieldItemProps={{ disabled: isLocked }} required={!isAdmin && orgPreferences.requiredFields?.includes("height")} />
              <HmsField name="weight" addonAfter="kg" fieldItemProps={{ disabled: isLocked }} required={!isAdmin && orgPreferences.requiredFields?.includes("weight")} />
            </HmsEntityFormGridBasic>
            <HmsEntityFormGridBasic>
              <HmsField name="phone" fieldItemProps={{ disabled: isLocked }} required={!isAdmin && orgPreferences.requiredFields?.includes("phone")} />
              <HmsField 
                name="email" 
                label={
                  <span className="flex items-center gap-2">
                    Email
                    {emailDirty && relationsData?.User?.userId && (
                      <Tooltip title="Changing the email will also update the associated user account email.">
                        <WarningOutlined className="text-yellow-500 text-sm" />
                      </Tooltip>
                    )}
                  </span>
                }
                validationLabel="Email"
                fieldItemProps={{ 
                  disabled: isLocked,
                  onChange: (e) => {
                    const newValue = e.target.value;
                    setEmailDirty(newValue !== data?.email);
                  }
                }} 
                required={!isAdmin && orgPreferences.requiredFields?.includes("email")} 
              />
            </HmsEntityFormGridBasic>
            <HmsEntityFormGridBasic>
              <HmsField name="countryBirth" type="COUNTRY_LIST" label="Country of Birth" fieldItemProps={{ disabled: isLocked }} required={!isAdmin && orgPreferences.requiredFields?.includes("countryBirth")} />
              <HmsField name="cityBirth" label="City of Birth" fieldItemProps={{ disabled: isLocked }} required={!isAdmin && orgPreferences.requiredFields?.includes("cityBirth")} />
            </HmsEntityFormGridBasic>
            <HmsEntityFormGridBasic>
              <HmsField name="country" type="COUNTRY_LIST" fieldItemProps={{ disabled: isLocked }} />
              <HmsField name="city" fieldItemProps={{ disabled: isLocked }} />
            </HmsEntityFormGridBasic>
            <h4>External relations</h4>
            <HmsEntityFormGridBasic>
              {isAdmin &&
                <HmsField name="externalId" fieldItemProps={{ disabled: isLocked }} />
              }
              <HmsField name="publicProfileUrl" fieldItemProps={{ disabled: isLocked }} required={!isAdmin && orgPreferences.requiredFields?.includes("publicProfileUrl")} />
            </HmsEntityFormGridBasic>
            {needsToSelectTeam &&
              <>
                <h4>Teams</h4>
                <HmsEntityFormGridBasic>
                  <HmsField
                    name="Teams"
                    label="Team Membership"
                    type="CHECKBOX_GROUP"
                    required={true}
                    fieldItemProps={{ dataType: 'ARRAY' }}
                    options={teamsData?.map((team, idx) => ({
                      value: team.teamId,
                      label: <div className="flex items-center gap-2 my-1 w-full">
                        <EntityLogo entity={team} size={24} useExperimentalBgStyle={true} />
                        <span>{team.name}</span>
                      </div>,
                    }))}
                  />
                </HmsEntityFormGridBasic>
              </>
            }
          </>
        )}
        footer={id != '_new_' && (
          <div className="mt-4">
            <div className="mt-4" ref={tabbedTablesParentRef}>
              <HmsTabbedTables
                ref={tabbedTablesRef}
                tables={[
                  ...(data?.status != 'DRAFT' ? [
                    {
                      key: 'Games',
                      title: "Games" + (gamesData ? (' (' + gamesData.length + ')') : ''),
                      children: <PlayerGames gamesData={gamesData} />
                    },
                  ] : []),
                  {
                    key: 'Teams',
                    title: "Teams" + (relationsTeamsOnly?.Teams ? (' (' + relationsTeamsOnly?.Teams?.length + ')') : ''),
                    children: <PlayerTeams data={relationsTeamsOnly?.Teams} sourceEntityId={data?.playerId} sourceEntityData={data} onChange={reloadRelationsTeamsOnly} />,
                  },
                  {
                    key: 'Jerseys',
                    title: "Jerseys" + (relationsData?.Jerseys ? (' (' + relationsData?.Jerseys?.length + ')') : ''),
                    children: <PlayerJerseys data={relationsData?.Jerseys} sourceEntityId={data?.playerId} sourceEntityData={data} onChange={reloadRelations} teams={relationsTeamsOnly?.Teams} />,
                  },
                  {
                    key: 'Positions',
                    title: "Positions" + (relationsData?.ListPositions ? (' (' + relationsData?.ListPositions?.length + ')') : ''),
                    children: <PlayerPositions data={relationsData?.ListPositions} sourceEntityId={data?.playerId} sourceEntityData={data} onChange={reloadRelations} />,
                  },

                  isAdmin && {
                    key: 'Sponsors',
                    title: "Sponsors" + (relationsData?.Sponsors ? (' (' + relationsData?.Sponsors?.length + ')') : ''),
                    children: <PlayerSponsors data={relationsData?.Sponsors} sourceEntityId={data?.playerId} onChange={reloadRelations} />
                  },
                  isAdmin && {
                    key: 'Awards',
                    title: "Awards" + (relationsData?.Awards ? (' (' + relationsData?.Awards?.length + ')') : ''),
                    children: <PlayerAwards data={relationsData?.Awards} sourceEntityId={data?.playerId} onChange={reloadRelations} />
                  },
                  isAdmin && {
                    key: 'Disciplinaries',
                    title: "Disciplinaries" + (relationsDisciplinariesOnly?.Disciplinaries ? (' (' + relationsDisciplinariesOnly?.Disciplinaries?.length + ')') : ''),
                    children: <PlayerDisciplinary data={relationsDisciplinariesOnly?.Disciplinaries} sourceEntityId={data?.playerId} onChange={reloadRelationsDisciplinariesOnly} />
                  },
                  ...(organization?.seasonPlayerRegistrationEnabled ? [{
                    key: 'SeasonRegistrations',
                    title: `Season Registrations${seasonRegistrationsCount ? ' (' + seasonRegistrationsCount + ')' : ''}`,
                    children: <PlayerSeasonPlayerRegistrations key={seasonRegistrationsKey} playerId={data?.playerId} playerData={data} onSeasonRegistrationChange={() => {
                      setSeasonRegistrationsKey(prev => prev + 1);
                      reloadSeasonRegistrations();
                    }} onPlayerDataReload={reload} />,
                  }] : []),
                  {
                    key: 'Attachments',
                    title: "Attachments" + (attachmentsData?.Attachments ? (' (' + attachmentsData?.Attachments?.length + ')') : ''),
                    children: <EntityAttachments entity={entity.name} id={data?.[entity.primaryKey]} data={attachmentsData} reload={reloadAttachments} />,
                  },
                  {
                    key: 'ChangeRequests',
                    title: "Change Requests" + (changeRequestsData?.ChangeRequests ? (' (' + (unprocessedChangeRequestCount > 0 ? '' + unprocessedChangeRequestCount + '/' : '') + changeRequestsData?.ChangeRequests?.length + ')') : ''),
                    children: <EntityChangeRequests sourceEntityName={entity.name} sourceEntityId={data?.[entity.primaryKey]} data={changeRequestsData?.ChangeRequests} onChange={reloadChangeRequests} />,
                    showDot: showChangeRequestDot,
                  },
                ].filter(t => t)}
              />
            </div>
          </div>
        )}
      />
    </>
  );
}

export default Player
